import React, { useRef, useState } from 'react';
import { Download, Copy, Check, Briefcase, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

/**
 * Recruitment ad creative — "Regional Field Specialists / Partners".
 *
 * Premium positioning — Measured Livestock is a high-end professional
 * trait collection service. The ad targets accomplished livestock
 * professionals who want to build a regional practice under our brand.
 *
 * Two formats:
 *   1) Feed Portrait:   1080 × 1350
 *   2) Feed Landscape:  1200 × 630
 */
const ContractorRecruitAd = () => {
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0f2818',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `Regional Field Specialists — New Zealand

Measured Livestock is appointing Regional Field Specialists to deliver our trait collection service to farmers in their region.

This is a professional opportunity to lead the on-farm side of our service — weighing, DNA sampling, herd reads and certified Body Condition Scoring — under the Measured Livestock banner, with the room to build your own numbers in your region.

Who we're looking for:
•  Senior stock people, livestock service providers or agencies looking to add something additional to their current practice

What we offer:
•  Practical industry know-how and existing customers behind you
•  The opportunity to build your own client base and grow your numbers
•  Full technical training, equipment support and quality systems
•  A defined regional territory you can grow on your terms

We're advertising NZ wide — specific regions are currently available. Keen to hear from you regardless of location, so get in touch and let's start the conversation.

Expressions of interest: measuredlivestock.com  ·  info@measuredlivestock.co.nz

#NZAgriculture #DairyNZ #BeefNZ #LivestockServices #RegionalPartnership #MeasuredLivestock`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Premium rural hero
  const heroImg = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1600&q=80';

  // ---------- styled inline blocks (re-used in both creatives) ----------
  const featurePill = (label) => (
    <div style={{
      background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)',
      padding: '16px 20px', borderRadius: 10, color: 'white',
      fontSize: 18, fontWeight: 600, textAlign: 'left', letterSpacing: 0.2,
    }}>{label}</div>
  );

  return (
    <section className="bg-gradient-to-br from-[#0f2818] via-[#1a4d2e] to-[#0f2818] py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            New · Professional Recruitment Ad
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <Briefcase className="w-9 h-9 text-[#ff6b00]" />
            Regional Field Specialists
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Premium recruitment creative — positioned for accomplished livestock professionals.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* === PORTRAIT 1080 × 1350 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-field-specialists-1080x1350.png')}
                disabled={downloading === 'measuredlivestock-field-specialists-1080x1350.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-contractor-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-field-specialists-1080x1350.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080,
                  height: 1350,
                  marginRight: -1080 * 0.58,
                  marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080,
                    height: 1350,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(180deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.86) 38%, rgba(15,40,24,0.95) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  {/* Brand corner */}
                  <div style={{
                    position: 'absolute', top: 38, left: 56, display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, opacity: 0.9, textTransform: 'uppercase' }}>
                      Measured Livestock
                    </span>
                  </div>

                  {/* Top right pill */}
                  <div style={{
                    position: 'absolute', top: 32, right: 56,
                    background: 'rgba(255,107,0,0.12)', border: '1.5px solid #ff6b00',
                    color: '#ff8533', padding: '10px 22px', borderRadius: 999,
                    fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
                  }}>
                    NZ Wide · Specific Regions Available
                  </div>

                  {/* Headline block */}
                  <div style={{ position: 'absolute', top: 130, left: 56, right: 56, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.85, letterSpacing: 6, textTransform: 'uppercase', marginBottom: 18 }}>
                      A Professional Opportunity
                    </div>
                    <h1 style={{
                      fontSize: 88, lineHeight: 1.0, fontWeight: 800, margin: 0,
                      letterSpacing: -1.8, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      Regional
                    </h1>
                    <h1 style={{
                      fontSize: 88, lineHeight: 1.0, fontWeight: 800, margin: '4px 0 18px',
                      color: '#ff8533', letterSpacing: -1.8,
                    }}>
                      Field Specialists
                    </h1>
                    <div style={{ fontSize: 26, opacity: 0.94, fontWeight: 400, lineHeight: 1.45, maxWidth: 820, margin: '0 auto' }}>
                      Lead the on-farm delivery of our trait collection service in your region —
                      with the room to build your own numbers under the Measured Livestock banner.
                    </div>
                  </div>

                  {/* Mid section — who we want */}
                  <div style={{
                    position: 'absolute', top: 620, left: 56, right: 56,
                    background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,107,0,0.5)',
                    borderRadius: 16, padding: '28px 32px',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#ff8533', letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14 }}>
                      Who we're looking for
                    </div>
                    <div style={{ fontSize: 21, fontWeight: 500, lineHeight: 1.55, opacity: 0.95 }}>
                      Senior stock people, livestock service providers or agencies looking to add
                      something additional to their current practice.
                    </div>
                  </div>

                  {/* What we offer */}
                  <div style={{
                    position: 'absolute', top: 870, left: 56, right: 56,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#ff8533', letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14, textAlign: 'center' }}>
                      What we offer
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      {featurePill('Practical industry know-how')}
                      {featurePill('Existing customer base')}
                      {featurePill('Training & equipment support')}
                      {featurePill('Build your own numbers')}
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div style={{
                    position: 'absolute', bottom: 56, left: 56, right: 56, textAlign: 'center',
                  }}>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '22px 0', borderRadius: 10,
                      fontSize: 30, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      Expressions of Interest
                    </div>
                    <div style={{ marginTop: 14, fontSize: 22, fontWeight: 700 }}>
                      measuredlivestock.com
                    </div>
                    <div style={{ marginTop: 4, fontSize: 16, opacity: 0.8, letterSpacing: 1.5 }}>
                      info@measuredlivestock.co.nz
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === LANDSCAPE 1200 × 630 === */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-field-specialists-1200x630.png')}
                disabled={downloading === 'measuredlivestock-field-specialists-1200x630.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-contractor-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-field-specialists-1200x630.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)',
                  width: 1200,
                  height: 630,
                  marginRight: -1200 * 0.45,
                  marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200,
                    height: 630,
                    position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    backgroundImage: `linear-gradient(110deg, rgba(15,40,24,0.96) 0%, rgba(15,40,24,0.90) 50%, rgba(15,40,24,0.55) 100%), url(${heroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white',
                  }}
                >
                  {/* Top brand strip */}
                  <div style={{ position: 'absolute', top: 36, left: 56, display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 6, height: 32, background: '#ff6b00' }} />
                    <span style={{ fontSize: 13, letterSpacing: 5, fontWeight: 700, textTransform: 'uppercase', opacity: 0.92 }}>
                      Measured Livestock · Careers
                    </span>
                  </div>

                  <div style={{
                    position: 'absolute', top: 36, right: 56,
                    background: 'rgba(255,107,0,0.12)', border: '1.5px solid #ff6b00',
                    color: '#ff8533', padding: '8px 18px', borderRadius: 999,
                    fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
                  }}>
                    NZ Wide · Specific Regions Available
                  </div>

                  {/* Left text block */}
                  <div style={{ position: 'absolute', top: 120, left: 56, right: 460 }}>
                    <div style={{ fontSize: 22, fontWeight: 600, opacity: 0.88, marginBottom: 8, letterSpacing: 4, textTransform: 'uppercase' }}>
                      A Professional Opportunity
                    </div>
                    <h1 style={{
                      fontSize: 64, lineHeight: 1.0, fontWeight: 800, margin: 0,
                      letterSpacing: -1.4,
                    }}>
                      Regional Field
                    </h1>
                    <h1 style={{
                      fontSize: 64, lineHeight: 1.0, fontWeight: 800, margin: '4px 0 18px',
                      color: '#ff8533', letterSpacing: -1.4,
                    }}>
                      Specialists Wanted
                    </h1>
                    <div style={{ fontSize: 18, fontWeight: 400, opacity: 0.94, lineHeight: 1.5, maxWidth: 660 }}>
                      Practical industry know-how and existing customers behind you —
                      with the room to build your own numbers in your region.
                    </div>
                  </div>

                  {/* Right column — who we want */}
                  <div style={{
                    position: 'absolute', top: 130, right: 56, width: 380,
                    background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,107,0,0.55)',
                    borderRadius: 14, padding: '22px 24px',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#ff8533', letterSpacing: 4, textTransform: 'uppercase', marginBottom: 12 }}>
                      Who we're looking for
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.55, opacity: 0.95 }}>
                      Senior stock people, livestock service providers or agencies looking to add
                      something additional to their current practice.
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{
                    position: 'absolute', bottom: 36, left: 56, right: 56,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                        Expressions of interest
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 800 }}>measuredlivestock.com</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '18px 32px', borderRadius: 8,
                      fontSize: 20, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 10px 28px rgba(0,0,0,0.4)',
                    }}>
                      Register Interest →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ad caption */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <Briefcase className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Recruitment Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-contractor-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Targeting tip:</strong> Location: <strong>New Zealand</strong>.
            Interests: <em>Dairy farming, Beef cattle, Livestock services, NAIT, Federated Farmers, Agribusiness</em>.
            Job titles: <em>Stock manager, Livestock agent, Herd tester, Farm advisor, Rural professional</em>.
            Run as a <strong>Lead-Generation campaign</strong> — applications land straight in your inbox.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContractorRecruitAd;
